import { FadeScroller } from "@components/atoms/FadeScroller"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Chip } from "@components/ui/Chip"
import { Icon, IconCart, IconName, IconRef } from "@components/ui/Icon"
import { useFindSession } from "@features/program/useProgramData"
import { FilmSessionFragment, FilmTileFragment } from "@generated-types"
import { formatDateTime } from "@utils/date-format"
import { cn } from "@utils/tw"
import { ReactNode } from "react"

export function MicroSessionTileFromHash(props: { filmId: string | number } & Omit<Props, "session" | "film">) {
  const item = useFindSession(props.filmId)

  if (!item) return null
  return <MicroSessionTile session={item.session} film={item.film} {...props} />
}

type Props = {
  session: FilmSessionFragment
  film: FilmTileFragment
  tags?: ReactNode
  showDate?: boolean
  showVenue?: boolean
  className?: string
  isInCart?: boolean
  isPurchased?: boolean
  inPlannerPanel?: boolean
}

export function MicroSessionTile(props: Props) {
  const tags = (
    <>
      {props.isInCart && <IconBadge icon="cart" label="In Cart" />}
      {props.isPurchased && <IconBadge icon="ticket" label="Purchased" />}
      {props.showVenue && props.session.info?.venue?.titleSimple && (
        <Chip shape="rounded" variant="high" className="bg-bg-high/30">
          {props.session.info?.venue?.titleSimple}
        </Chip>
      )}
      {props.showDate && (
        <Chip shape="rounded" variant="high" className="bg-bg-high/30">
          {formatDateTime(props.session.info?.dateTime ?? "")}
        </Chip>
      )}
      {props.tags}
    </>
  )

  return (
    <div className={cn("flex w-full items-center gap-3", props.className)}>
      <div className="bg-fg/5 rounded-sm flex-none w-[60px]">
        <ResponsiveImage
          srcSet={props.film.media?.thumb?.srcSet}
          aspect={4 / 3}
          className="w-full overflow-hidden rounded-[4px]"
        />
      </div>
      <div className="flex flex-1 flex-col gap-[0.3em]">
        <div className="type-ui-s line-clamp-1">{props.film.title}</div>
        <div className="flex flex-1 flex-col gap-2 min-w-0">
          {props.inPlannerPanel ? (
            <FadeScroller className="w-full type-label-s min-h-[2em] overscroll-auto">
              <div className="absolute flex w-min items-center gap-[0.5em]">{tags}</div>
            </FadeScroller>
          ) : (
            <div className="flex items-center gap-[0.5em]">{tags}</div>
          )}
        </div>
      </div>
    </div>
  )
}

function IconBadge(props: { icon: IconRef; label: string }) {
  return (
    <span className="type-label-s inline-flex gap-[0.4em] whitespace-nowrap items-center justify-center">
      <Icon name={props.icon} className="size-[1.3em]" />
      <span>{props.label}</span>
    </span>
  )
}
